import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { apiGet } from '../lib/api';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface LoyaltyDashboardData {
  programId: string;
  profile: {
    firstName: string;
    lastName: string;
    avatarUrl: string | null;
    tierCode: string | null;
    tierName: string | null;
    tierColor: string | null;
    pointsBalance: number;
    lifetimePoints: number;
    enrolledAt: string | null;
  };
  tierProgress: {
    current: { name: string; minPoints: number; color: string } | null;
    next: { name: string; minPoints: number; color: string } | null;
    progressPercent: number;
    pointsNeeded: number;
  };
  stats: {
    currentStreak: number;
    globalRank: number | null;
    totalParticipants: number;
    unlockedBadges: number;
    totalBadges: number;
    challengesWon: number;
  };
  recentActivity: Array<{
    type: string;
    title: string;
    points: number;
    occurredAt: string;
    icon?: string;
  }>;
  missionsToday: { total: number; completed: number; missions: unknown[] };
  activeFlashReward: {
    id: string;
    title: string;
    description: string | null;
    imageUrl: string | null;
    endsAt: string;
    rewardType: string;
  } | null;
  friendStandings: Array<{
    customerId: string;
    firstName: string | null;
    lastName: string | null;
    avatarUrl: string | null;
    points: number;
    rank: number;
  }>;
}

interface LoyaltyState {
  data: LoyaltyDashboardData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// ─── Context ─────────────────────────────────────────────────────────────────

const LoyaltyContext = createContext<LoyaltyState>({
  data: null,
  loading: false,
  error: null,
  refetch: () => {},
});

export function LoyaltyProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [data, setData] = useState<LoyaltyDashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchKey, setFetchKey] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    apiGet<LoyaltyDashboardData>('/customer/loyalty/dashboard-full')
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) {
          if ((err as { status?: number }).status === 404) {
            // Customer has no loyalty account yet — not an error, just not enrolled
            setData(null);
          } else {
            setError(err.message ?? 'Failed to load loyalty data');
          }
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, fetchKey]);

  const refetch = () => setFetchKey((k) => k + 1);

  return (
    <LoyaltyContext.Provider value={{ data, loading, error, refetch }}>
      {children}
    </LoyaltyContext.Provider>
  );
}

export function useLoyalty(): LoyaltyState {
  return useContext(LoyaltyContext);
}
